import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout/Layout';
import { toast } from 'react-toastify';
import Seo from '../../components/Seo';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const statusColors = { Open: 'primary', 'In Progress': 'warning', Resolved: 'success', Closed: 'secondary' };

const SupportTickets = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}tickets.php?user_id=${user.id}`);
            const tickData = await res.json();
            setTickets(Array.isArray(tickData) ? tickData : []);
        } catch (error) {
            toast.error(t('support.failedLoad') || 'Failed to load tickets');
        }
        setLoading(false);
    }, [t, user]);

    useEffect(() => {
        if (user) fetchData();
    }, [user, fetchData]);

    const stats = {
        open: tickets.filter(t => t.status === 'Open' || t.status === 'In Progress').length,
        resolved: tickets.filter(t => t.status === 'Resolved').length,
        closed: tickets.filter(t => t.status === 'Closed').length
    };

    return (
        <Layout hideScrollbar>
            <Seo title={t('support.center')} />
            <main className="page_content pt-150 pb-120">
                <div className="container">
                    <div className="d-flex justify-content-between align-items-center mb-5">
                        <div className="admin-header">
                            <h1 className="text-white display-5 mb-2">{t('support.center')}</h1>
                            <p className="text-muted">{t('support.trackManage')}</p>
                        </div>
                        <Link to="/support/create" className="btn-create-ticket">
                            <i className="fas fa-plus-circle me-2"></i> {t('support.newRequest')}
                        </Link>
                    </div>

                    {/* Status Cards */}
                    <div className="row mb-5">
                        <div className="col-lg-4 col-md-6 mb-4">
                            <div className="stat-card p-4 d-flex align-items-center gap-3">
                                <div className="stat-icon bg-primary"><i className="fas fa-envelope-open"></i></div>
                                <div>
                                    <h3 className="text-white mb-0">{stats.open}</h3>
                                    <p className="text-muted small mb-0">{t('support.openTickets')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 mb-4">
                            <div className="stat-card p-4 d-flex align-items-center gap-3">
                                <div className="stat-icon bg-success"><i className="fas fa-check-circle"></i></div>
                                <div>
                                    <h3 className="text-white mb-0">{stats.resolved}</h3>
                                    <p className="text-muted small mb-0">{t('support.resolvedTickets')}</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 mb-4">
                            <div className="stat-card p-4 d-flex align-items-center gap-3">
                                <div className="stat-icon bg-secondary"><i className="fas fa-archive"></i></div>
                                <div>
                                    <h3 className="text-white mb-0">{stats.closed}</h3>
                                    <p className="text-muted small mb-0">{t('support.closedArchived')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="support-card p-4">
                                <h4 className="text-white mb-4"><i className="fas fa-list me-2 text-primary"></i> {t('support.yourInquiries')}</h4>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                    </div>
                                ) : tickets.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="fas fa-ticket-alt fa-4x text-white-10 mb-3"></i>
                                        <p className="text-white-50">{t('support.noTicketsFound')}</p>
                                    </div>
                                ) : (
                                    <div className="tickets-list">
                                        {tickets.map(ticket => (
                                            <Link key={ticket.id} to={`/support/ticket/${ticket.id}`} className="ticket-item d-block p-4 mb-3 text-decoration-none">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center gap-4 flex-grow-1">
                                                        <div className={`status-dot bg-${statusColors[ticket.status]}`}></div>
                                                        <div>
                                                            <h5 className="text-white mb-1 fw-bold">#{ticket.id} — {ticket.subject}</h5>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <span className="text-primary small fw-bold text-uppercase">{ticket.category_name}</span>
                                                                <span className="text-muted smallest"><i className="far fa-clock me-1"></i> {new Date(ticket.created_at).toLocaleString()}</span>
                                                                {ticket.comment_count > 0 && (
                                                                    <span className="badge bg-dark border border-white-10">
                                                                        <i className="fas fa-comment me-1"></i>{ticket.comment_count}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-end">
                                                        <span className={`badge px-3 py-2 rounded-pill bg-${statusColors[ticket.status]}`}>{ticket.status}</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <style>{`
                .admin-header { border-left: 4px solid #6c5ce7; padding-left: 30px; }
                .support-card { background: rgba(255,255,255,0.03); border-radius: 20px; border: 1px solid rgba(255,255,255,0.06); }
                .stat-card { background: rgba(255,255,255,0.03); border-radius: 20px; border: 1px solid rgba(255,255,255,0.07); transition: 0.3s; }
                .stat-icon { width: 50px; height: 50px; border-radius: 15px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; }
                .btn-create-ticket { background: #6c5ce7; color: #fff; border: none; padding: 12px 25px; border-radius: 30px; font-weight: 700; text-decoration: none; transition: 0.3s; }
                .btn-create-ticket:hover { background: #5a4ad1; transform: translateY(-3px); box-shadow: 0 10px 20px rgba(108, 92, 231, 0.3); color: #fff; }
                .ticket-item { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 16px; transition: 0.25s; }
                .ticket-item:hover { border-color: #6c5ce7; background: rgba(255,255,255,0.05); transform: translateY(-3px); box-shadow: 0 10px 30px rgba(0,0,0,0.2); }
                .status-dot { width: 10px; height: 10px; border-radius: 50%; }
                .smallest { font-size: 11px; }
                .text-white-10 { color: rgba(255,255,255,0.1); }
                .border-white-10 { border-color: rgba(255,255,255,0.1) !important; }
            `}</style>
        </Layout>
    );
};

export default SupportTickets;
